import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "../lib/api";
import AppShell from "../components/AppShell";
import CreateEventDialog from "../components/CreateEventDialog";
import { Plus, Ticket, CheckCircle, XCircle, Calendar, Receipt, Trash, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function StatusDot({ status }) {
  const map = { ACTIVE: "bg-[#00FF66]", PAUSED: "bg-[#FF9900]", ARCHIVED: "bg-white/30", DRAFT: "bg-[#9D00FF]" };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status] || "bg-white/30"}`} />;
}

export default function Dashboard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/events");
      setEvents(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const deleteEvent = async (e, event) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${event.title}"? This removes tickets, members and requests. Cannot be undone.`)) return;
    setDeletingId(event.event_id);
    try {
      await api.delete(`/events/${event.event_id}`);
      toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeletingId(null);
    }
  };

  const totals = events.reduce((acc, e) => {
    acc.cap += e.ticket_cap || 0;
    acc.issued += e.issued_count || 0;
    acc.paid += e.paid_count || 0;
    acc.redeemed += e.redeemed_count || 0;
    return acc;
  }, { cap: 0, issued: 0, paid: 0, redeemed: 0 });

  return (
    <AppShell>
      {/* Hero header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden border border-white/10 bg-gradient-to-br from-[#0A0A0C] via-[#0A0A0C] to-[#00FF66]/[0.06] mb-8"
      >
        <div className="absolute inset-0 aura-grid opacity-30 pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#00FF66]/10 blur-3xl" />
        <div className="relative p-8 md:p-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Console · Events</div>
            <h1 className="heading-font text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3">
              Your Events
            </h1>
            <p className="mono-font text-xs text-white/50 mt-3 max-w-md">
              Every event is a cryptographically anchored inventory system. Create, distribute, verify.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="create-event-btn"
            className="group relative inline-flex items-center gap-3 px-6 py-4 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 self-start"
          >
            <Plus weight="bold" size={16} />
            Create Event
            <span className="absolute inset-0 border border-[#00FF66]/40 -m-1 group-hover:m-0 transition-all duration-300 pointer-events-none" />
          </button>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {[
          { label: "Total Capacity", value: totals.cap, icon: Ticket, color: "text-white", accent: "border-white/20" },
          { label: "Issued", value: totals.issued, icon: Receipt, color: "text-[#FF0033]", accent: "border-[#FF0033]/20" },
          { label: "Paid", value: totals.paid, icon: CheckCircle, color: "text-[#00FF66]", accent: "border-[#00FF66]/20" },
          { label: "Redeemed", value: totals.redeemed, icon: XCircle, color: "text-[#9D00FF]", accent: "border-[#9D00FF]/20" },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
              className={`border ${s.accent} bg-[#0A0A0C] p-5 hover:bg-white/[0.02] transition-colors duration-200`}
            >
              <div className="flex items-center justify-between">
                <div className="mono-font text-[10px] tracking-[0.25em] uppercase text-white/50">{s.label}</div>
                <Icon size={16} weight="duotone" className={s.color} />
              </div>
              <div className={`heading-font text-3xl font-black tracking-tighter mt-3 ${s.color}`}>
                {s.value.toLocaleString()}
              </div>
            </motion.div>
          );
        })}
      </div>

      {loading ? (
        <div className="mono-font text-sm text-white/40 py-16 text-center">Loading events…</div>
      ) : events.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="border border-dashed border-white/15 p-16 text-center"
        >
          <Calendar size={40} weight="duotone" className="mx-auto text-white/40" />
          <div className="heading-font text-xl font-bold mt-4">No events yet</div>
          <div className="mono-font text-xs text-white/50 mt-2">Create your first cryptographic event.</div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="create-event-empty-btn"
            className="mt-6 inline-flex items-center gap-3 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200"
          >
            <Plus weight="bold" size={16} />
            Create Event
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {events.map((e, i) => {
              const pct = e.ticket_cap ? Math.round((e.allocated_count / e.ticket_cap) * 100) : 0;
              const paidPct = e.ticket_cap ? Math.round((e.paid_count / e.ticket_cap) * 100) : 0;
              return (
                <motion.div
                  key={e.event_id}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
                  whileHover={{ y: -4 }}
                  className="relative group"
                >
                  <Link
                    to={`/events/${e.event_id}`}
                    data-testid={`event-card-${e.event_id}`}
                    className="block border border-white/10 hover:border-[#00FF66]/60 transition-colors duration-200 bg-[#0A0A0C] overflow-hidden"
                  >
                    <div className="aspect-[16/9] bg-black relative overflow-hidden border-b border-white/10">
                      {e.poster_url || e.design_url ? (
                        <img src={e.poster_url || e.design_url} alt={e.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center aura-grid">
                          <Ticket size={48} weight="duotone" className="text-white/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2 py-1 bg-black/70 border border-white/20 mono-font text-[9px] tracking-[0.25em] uppercase">
                        <StatusDot status={e.status} />
                        {e.status}
                      </div>
                      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#00FF66] text-black mono-font text-[9px] tracking-widest">
                          Open <ArrowUpRight size={10} weight="bold" />
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="heading-font text-lg font-bold tracking-tight truncate">{e.title}</div>
                      <div className="mono-font text-[10px] text-white/40 mt-1 truncate">
                        {e.host ? `Hosted by ${e.host}` : (e.venue || "—")}
                      </div>
                      {e.host && e.venue && (
                        <div className="mono-font text-[10px] text-white/30 truncate">{e.venue}</div>
                      )}
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        <div>
                          <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Cap</div>
                          <div className="mono-font text-sm text-white">{e.ticket_cap}</div>
                        </div>
                        <div>
                          <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Issued</div>
                          <div className="mono-font text-sm text-[#FF0033]">{e.issued_count}</div>
                        </div>
                        <div>
                          <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Paid</div>
                          <div className="mono-font text-sm text-[#00FF66]">{e.paid_count}</div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="h-1 bg-white/10 relative overflow-hidden">
                          <div className="absolute inset-y-0 left-0 bg-[#00FF66]/50" style={{ width: `${pct}%` }} />
                          <div className="absolute inset-y-0 left-0 bg-[#00FF66]" style={{ width: `${paidPct}%` }} />
                        </div>
                        <div className="mono-font text-[9px] text-white/40 mt-2 uppercase tracking-widest">
                          {pct}% allocated · {paidPct}% paid
                        </div>
                      </div>
                    </div>
                  </Link>
                  <button
                    onClick={(evt) => deleteEvent(evt, e)}
                    disabled={deletingId === e.event_id}
                    data-testid={`delete-event-${e.event_id}`}
                    className="absolute top-3 right-3 p-1.5 bg-black/70 border border-white/20 opacity-0 group-hover:opacity-100 hover:border-[#FF0033]/60 hover:text-[#FF0033] transition-all duration-200 disabled:opacity-30"
                    aria-label="Delete event"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      <CreateEventDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          load();
        }}
      />
    </AppShell>
  );
}
